// ===========================================
// COMPONENTE: VencimientosTabla
// ===========================================
// Tabla densa de vencimientos con TanStack Table
// Paginación y marcar presentado

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type SortingState,
  type PaginationState,
} from '@tanstack/react-table'
import { useState, useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'
import type { VencimientoConCliente } from '@/types/vencimientos'
import type { CatalogoImpuesto } from '@/types/impuestos'
import { createVencimientosColumns } from './table/vencimientosColumns'

interface VencimientosTablaProps {
  vencimientos: VencimientoConCliente[]
  onMarcarPresentado: (vencimiento: VencimientoConCliente) => void
  onVerDetalles?: (vencimiento: VencimientoConCliente) => void
  onRegistrarPago?: (vencimiento: VencimientoConCliente) => void
  onRegistrarVEP?: (vencimiento: VencimientoConCliente) => void
  onPlanPago?: (vencimiento: VencimientoConCliente) => void
  onDescartar?: (vencimiento: VencimientoConCliente) => void
  onRestaurar?: (vencimiento: VencimientoConCliente) => void
  onRevertirPresentado?: (vencimiento: VencimientoConCliente) => void
  onReenviarNotificacion?: (vencimiento: VencimientoConCliente) => void
  onProgramarAlerta?: (vencimiento: VencimientoConCliente) => void
  /** ORR-230 fase 5: abre la ficha del cliente al clickear su nombre */
  onAbrirCliente?: (vencimiento: VencimientoConCliente) => void
  /** ORR-232: claves de los vencimientos con alerta del usuario actual (ver claveAlerta) */
  alertasAncla?: Set<string>
  catalogo?: CatalogoImpuesto[]
  titulo?: string
  isLoading?: boolean
}

export function VencimientosTabla({
  vencimientos,
  onMarcarPresentado,
  onVerDetalles,
  onRegistrarPago,
  onRegistrarVEP,
  onPlanPago,
  onDescartar,
  onRestaurar,
  onRevertirPresentado,
  onReenviarNotificacion,
  onProgramarAlerta,
  onAbrirCliente,
  alertasAncla,
  catalogo,
  titulo = 'Vencimientos',
  isLoading = false,
}: VencimientosTablaProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'fecha_vencimiento', desc: false },
  ])
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  const columns = useMemo(
    () => createVencimientosColumns(onMarcarPresentado, onVerDetalles, catalogo, {
      onRegistrarPago,
      onRegistrarVEP,
      onPlanPago,
      onDescartar,
      onRestaurar,
      onRevertirPresentado,
      onReenviarNotificacion,
      onProgramarAlerta,
      onAbrirCliente,
      alertasAncla,
    }),
    [onMarcarPresentado, onVerDetalles, onRegistrarPago, onRegistrarVEP, onPlanPago, onDescartar, onRestaurar, onRevertirPresentado, onReenviarNotificacion, onProgramarAlerta, onAbrirCliente, alertasAncla, catalogo]
  )

  const table = useReactTable({
    data: vencimientos,
    columns,
    getRowId: (row) => row.id ?? '',
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {titulo}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[400px] bg-muted animate-pulse rounded-lg" />
        </CardContent>
      </Card>
    )
  }

  if (vencimientos.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {titulo}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Calendar className="h-12 w-12 mb-2" />
            <p>No hay vencimientos</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const totalFiltered = table.getFilteredRowModel().rows.length
  const pageStart = pagination.pageIndex * pagination.pageSize + 1
  const pageEnd = Math.min(
    (pagination.pageIndex + 1) * pagination.pageSize,
    totalFiltered
  )

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Calendar className="h-5 w-5" />
          {titulo}
          <span className="text-sm font-normal text-muted-foreground">
            ({vencimientos.length})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {/* Table */}
        <Table className="w-full">
          <TableHeader className="bg-background">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    style={{ width: header.column.columnDef.size }}
                    className="h-9 px-3"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className="h-10 hover:bg-muted/50 transition-colors"
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{ width: cell.column.columnDef.size }}
                    className="py-1.5 px-3"
                  >
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Filas por página</span>
            <Select
              value={String(pagination.pageSize)}
              onValueChange={(v) =>
                setPagination((p) => ({
                  ...p,
                  pageSize: Number(v),
                  pageIndex: 0,
                }))
              }
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
            <span>
              {pageStart}-{pageEnd} de {totalFiltered}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm px-2">
              Página {table.getState().pagination.pageIndex + 1} de{' '}
              {table.getPageCount()}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
